import { Table } from 'antd';
import { useDispatch } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { Link, useLoaderData, useRevalidator } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { RoleEnum } from '../../../../../enums/role.enum';
import { GetUsersSuccessResponse } from '../../../../../types/api/get-users-success-response';
import { Company } from '../../../../../../companies/types/api/get-company-success-response';
import { showLoadingAlertMessage } from '../../../../../../../utils/show-loading-alert-message/show-loading-alert-message';
import { networkCall } from '../../../../../../../utils/network-call/network-call';
import { showDefaultErrorMessages } from '../../../../../../../utils/show-default-error-message/show-default-error-messages';
import { AlertMessageState, clearAlertMessage, showAlertMessage } from '../../../../../../../redux/reducer/alert-message.reducer';
import { AlertMessages } from '../../../../../../../enums/alert-messages.enum';
import { getImageUrl } from '../../../../../../../utils/image-helper/get-image-url';
import { setSelectedMenuItemKeys } from '../../../../../../../redux/reducer/antd-layout-info.reducer';
import useDebouncedValue from '../../../../../../../hooks/use-debonced-value';
import { SearchBar } from '../../../../../../../components/search-bar/search-bar';
import { CMSButton } from '../../../../../../../components/cms/button/cms-button.';

type TableDataType = {
    id: number;
    name: string;
    email: string;
    role: RoleEnum;
    company?: Company;
};

type DeleteColumnProps = {
    user: TableDataType;
    revalidate: () => void;
};

const DeleteColumn = ({ user: { id }, revalidate }: DeleteColumnProps) => {
    const dispatch = useDispatch();

    const onClick = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        if(window.confirm('Biztosan törlöd a felhasználót?')) {
            showLoadingAlertMessage();

            const response = await networkCall(`/users/${ id }`, {
                method: 'DELETE'
            });;

            const messageShowed = showDefaultErrorMessages(response);
            if(messageShowed) return;

            if(response?.status === 200) {
                revalidate();
                dispatch(showAlertMessage({
                    alertMessageType: 'success',
                    alertMessageText: 'Törlés sikeres.'
                }));
                return;
            }

            dispatch(clearAlertMessage());
            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };

            if(response?.status === 404) {
                alertMessageState.alertMessageText = 'Törlés sikertelen, a törlendő felhasználó nem létezik.';
            }

            dispatch(showAlertMessage(alertMessageState));
        }
    }

    return (
        <a onClick={onClick}>
            <img src={getImageUrl('delete-icon2.png')} alt="delete-icon2" />
        </a>
    );
}

export const AdminUserList = () => {
    const dispatch = useDispatch();
    const loaderData: any = useLoaderData();
    const revalidator = useRevalidator();

    useEffect(() => {
        dispatch(setSelectedMenuItemKeys(['admin-users']));
    }, [dispatch]);

    const [data, setData] = useState<TableDataType[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebouncedValue(searchTerm);

    const handleSearchBarChange = (value: string) => {
        setSearchTerm(value);
        setDebouncedSearchTerm(value);
    };

    useEffect(() => {
        const messageShowed = showDefaultErrorMessages(loaderData.getUsersResponse);
        if(messageShowed) return;

        if(loaderData.getUsersResponse?.status === 200) {
            const res: AxiosResponse<GetUsersSuccessResponse> = loaderData.getUsersResponse;
            const tableData: TableDataType[] = [];
            for (const user of res.data.users) {
                const name = user.name ?? '';
                const email = user.email ?? '';

                if (
                    name.toLocaleLowerCase().includes(debouncedSearchTerm.toLocaleLowerCase().trim()) ||
                    email.toLocaleLowerCase().includes(debouncedSearchTerm.toLocaleLowerCase().trim())
                )
                    tableData.push({
                        id: user.id,
                        name: user.name,
                        email: user.email,
                        role: user.role,
                        company: user?.company
                    });
            }

            setData(tableData);
            return;
        }

    }, [loaderData, debouncedSearchTerm]);
    
    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            sorter: (a: TableDataType, b: TableDataType) => a.id - b.id
        },
        {
            title: 'Név',
            dataIndex: 'name',
            sorter: (a: TableDataType, b: TableDataType) => String(a.name).localeCompare(b.name)
        },
        {
            title: 'Email cím',
            dataIndex: 'email',
            sorter: (a: TableDataType, b: TableDataType) => String(a.name).localeCompare(b.name)
        },
        {
            title: 'Jogosultság',
            render: (record: TableDataType) => {
                switch (record.role) {
                    case RoleEnum.Admin: return 'Admin';
                    case RoleEnum.CompanyAdmin: return 'Cégvezető';
                    case RoleEnum.Maintainer: return 'Karbantartási vezető';
                    case RoleEnum.User: return 'Felhasználó';
                    default: return 'Ismeretlen';
                }
            }
        },
        {
            title: 'Cég',
            sorter: (a: TableDataType, b: TableDataType) => String(a?.company?.name).localeCompare(b?.company?.name ?? ''),
            render: (record: TableDataType) => {
                return record?.company?.name;
            }
        },
        {
            title: 'Adatlap',
            render: (record: TableDataType) => {
                return (
                    <Link to={`/cms/admin/users/edit/${ record.id }`}>
                        <img src={getImageUrl('edit-icon.png')} alt="edit-icon" />
                    </Link>
                );
            }
        },
        /*{
            title: 'Kurzusai',
            render: (record: TableDataType) => {
                return (
                    <Link to={`/cms/admin/users/${ record.id }/courses`}>
                        <img src={getImageUrl('eye-icon.png')} alt="eye-icon" />
                    </Link>
                );
            }
        },*/
        {
            title: 'Törlés',
            render: (record: TableDataType) => {
                return (
                    <DeleteColumn user={record} revalidate={revalidator.revalidate}/>
                );
            }
        }
    ];

    return (
        <>
            <div className="page-title">Felhasználók</div>
            <div className='d-flex justify-content-between gap-3 mb-4'>
                <SearchBar value={searchTerm} onChange={handleSearchBarChange} />
                <Link to='/cms/admin/users/add'>
                    <CMSButton text='Új felhasználó hozzáadása' iconSrc={getImageUrl('user-icon-white.png')} />
                </Link>
            </div>
            <Table
                className='table-responsive data-table'
                columns={columns}
                dataSource={data}
                bordered
                pagination={
                    {
                        position: ['bottomRight'],
                        pageSize: 10
                    }
                }
            />
        </>
    );
};